import {
  ApiBearerToken,
  context,
  createStore,
  redirectToSignIn,
  savePacvueTokenToLocalStorage,
  setBearerToken,
  setErrorPage,
  updateLinkWithAccountId,
  useAccountPlans,
  useCredentials,
  useGTM,
  useQuery,
} from "@helium10/re-core";
import { FeaturesTranslateProvider, MainLayout, Menu } from "@helium10/re-features";
import type { IAccountId } from "@helium10/re-features/src/common/types/IAccountId";
import {
  DialogModal,
  FontsStyles,
  GlobalStyle,
  NewButton,
  NewStatusSwitcher,
  scrollStylesCSS,
  styledTheme,
  UiTranslateProvider,
  useProgressLoadStatus,
} from "@helium10/re-ui-components";
import { lazy, memo, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import styled, { createGlobalStyle, ThemeProvider } from "styled-components";

import { BatchUploadBanner } from "./components/BatchUploadBanner";
import { useUploadFilesStore } from "./components/UploadFiles/store";
import { DocumentCenterWhitelistProvider } from "./core/documentCenterWhitelist";
import { NotMatch } from "./core/hooks/useLink";
// import { useBeforeUnloadGuard } from "./hooks/useBeforeUnloadGuard";
import { useRedirect } from "./core/hooks/useRedirect";
import { useSetupCoralogix } from "./core/hooks/useSetupCoralogix";
import { getRedirectMrsRoute, mrsLink } from "./core/links";
import { useMenu } from "./features/modals/useMenu";
import { useTranslation } from "./i18n/useTranslation";
import { MRSTranslateProvider } from "./i18n/useTranslation";
import { useRefundGenieRequest } from "./store/refundGenieStore";
import eventBus from "./utils/eventBus";
import { setNavigate } from "./utils/navigateService";

const MRS = lazy(() =>
  import(/* webpackChunkName: "App" */ "./App").then((module) => ({
    default: module.MRS,
  })),
);

declare global {
  interface Window {
    eventBus: typeof eventBus;
  }
}
window.eventBus = eventBus;

const store = createStore();
window.setError = setErrorPage.patch;

const helmetContext = {};

const PageStyle = createGlobalStyle`
  html, body, #re-mrs {
    height: 100%;
    margin: 0;
  }
  body, div, ul {
    ${scrollStylesCSS};
  }
`;

if (import.meta.env.PROD) {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  window.packageVersions = packageVersions;
}

const useChangeListener = () => {
  const location = useLocation();
  const history = useRef<{
    from: null | string;
    to: null | string;
  }>({
    from: null,
    to: null,
  });
  const { setStatus } = useProgressLoadStatus();
  useEffect(() => {
    history.current.from = history.current.to;
    history.current.to = location.pathname.split("?")[0];
    if (history.current.from) {
      const [_1, fmodule, ...fmodules] = history.current.from.split("/");
      const [_2, tmodule, ...tmodules] = history.current.to.split("/");
      if (fmodule === tmodule && fmodules.join(",") !== tmodules.join(",")) {
        setStatus("finish");
      }
    }
  }, [location.pathname, setStatus]);
};

const Root = () => {
  const { t } = useTranslation();
  const { accountId } = useQuery<{ accountId: string }>();
  // useAccountPlans({ accountId });
  useSetupCoralogix();
  const token = useCredentials(accountId);
  const { getPath } = useRedirect();

  const { menu } = useMenu();
  const handleAccountChange = useCallback(
    async (accountId: IAccountId) => {
      let result;
      try {
        result = await ApiBearerToken.get(`${accountId}`);
      } catch (error) {
        redirectToSignIn();
        return Promise.reject(error);
      }
      const token = result.data.token;
      setBearerToken(`${accountId}`, token);
      await savePacvueTokenToLocalStorage(`${accountId}`, {
        pacvueToken: result.data.pacvueToken as string,
        pacvueRefreshToken: result.data.pacvueRefreshToken as string,
        pacvueTokenExpiresIn: result.data.pacvueTokenExpiresIn as number,
        pacvueTokenCreatedAt: Date.now(),
      });
      return () => {
        window.location.href = getPath(getRedirectMrsRoute(accountId.toString()), {
          accountId: accountId.toString(),
        });
      };
    },
    [getPath],
  );

  // 初始化导航服务(任意位置可调用导航,包括store中)
  const navigate: ReturnType<typeof useNavigate> = useNavigate();
  useEffect(() => {
    setNavigate(navigate);
  }, [navigate]);

  // useBeforeUnloadGuard();
  useChangeListener();
  const { setStatus } = useProgressLoadStatus();
  useEffect(() => {
    setStatus("start");
  }, [setStatus]);

  useEffect(() => {
    useRefundGenieRequest.action();
  }, []);

  // 控制点击导航按钮离开时的弹框提示用户是否确认离开
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false); // 控制离开页面的弹框是否显示
  const resolveRef = useRef<{ resolve: ((value: boolean) => void) | null }>({ resolve: null });
  const handleBeforeNavigate = useCallback(() => {
    const tasks = useUploadFilesStore.getState().tasks;
    const hasBulkUploading = Object.entries(tasks).some(
      ([taskId, task]) =>
        taskId.startsWith("document-center-batch-") && task.status === "uploading",
    );
    if (!hasBulkUploading) {
      return Promise.resolve(true);
    }
    return new Promise<boolean>((resolve) => {
      setShowLeaveConfirm(true);
      resolveRef.current.resolve = resolve;
    });
  }, []);

  const handleConfirm = useCallback(() => {
    setShowLeaveConfirm(false);
    useUploadFilesStore.getState().clearBulkUploadTasks();
    resolveRef.current.resolve?.(true);
    resolveRef.current.resolve = null;
  }, []);

  const handleCancel = useCallback(() => {
    setShowLeaveConfirm(false);
    resolveRef.current.resolve?.(false);
    resolveRef.current.resolve = null;
  }, []);

  return (
    <Suspense fallback={null}>
      <Menu />
      <MainLayout
        accountId={accountId ? Number(accountId) : undefined}
        logoutToken={token}
        onAccountChange={(accountId: IAccountId) => handleAccountChange(accountId)}
        additional={menu}
        layoutBanner={<BatchUploadBanner />}
        onBeforeNavigate={handleBeforeNavigate}
      >
        <Suspense fallback={null}>
          <NewStatusSwitcher>
            <Routes>
              <Route path={`${mrsLink}/*`} element={<MRS />} />
              <Route path={`/refund-genie`} element={<RGRedirect></RGRedirect>} />
              <Route path={`/refund-genie/:reportId`} element={<RGRedirect></RGRedirect>} />
              <Route path="*" element={<NotMatch />} />
            </Routes>
          </NewStatusSwitcher>
        </Suspense>
      </MainLayout>

      <DialogModal
        isOpen={showLeaveConfirm}
        onClose={handleCancel}
        title={t("batchUploadBanner.leaveConfirm.title")}
        titleAlign="center"
        footer={
          <Wrapper>
            <NewButton onClick={handleCancel} buttonType="cancel" size="100">
              {t("buttons.cancel")}
            </NewButton>
            <NewButton onClick={handleConfirm} buttonType="primary" size="100">
              {t("buttons.confirm")}
            </NewButton>
          </Wrapper>
        }
      >
        <Wrapper>{t("batchUploadBanner.leaveConfirm.content")}</Wrapper>
      </DialogModal>
    </Suspense>
  );
};

const RGRedirect = () => {
  const { reportId } = useParams();
  if (reportId) {
    return <Navigate to={updateLinkWithAccountId(`${mrsLink}/refundGenie/${reportId}`)} replace />;
  }
  return <Navigate to={updateLinkWithAccountId(`${mrsLink}/refundGenie`)} replace />;
};

export const GTM = memo(() => {
  useGTM();
  return null;
});

createRoot(document.getElementById("re-mrs")!).render(
  <context.Provider value={store}>
    <HelmetProvider context={helmetContext}>
      <GTM />
      <BrowserRouter>
        <ThemeProvider theme={styledTheme}>
          <MRSTranslateProvider>
            <DocumentCenterWhitelistProvider>
              <UiTranslateProvider>
                <FeaturesTranslateProvider>
                  <GlobalStyle />
                  <FontsStyles />
                  <PageStyle />
                  <Root />
                </FeaturesTranslateProvider>
              </UiTranslateProvider>
            </DocumentCenterWhitelistProvider>
          </MRSTranslateProvider>
        </ThemeProvider>
      </BrowserRouter>
    </HelmetProvider>
  </context.Provider>,
);

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  gap: ${({ theme }) => theme.spacing.sp12};
`;
